/* eslint-disable no-param-reassign */
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon, { IconSprite } from 'components/Icon/Icon';
import AutoSizeTextBoxInput from 'components/Inputs/AutoSizeTextBoxInput/AutoSizeTextBoxInput';
import {
  formatPhoneNumber,
  getFileExtension,
  getFileType,
  groupByDate,
  isRole,
  randomTempIdGenerator,
} from 'utils/utils';
import { sortWith, prop, ascend, isEmpty } from 'ramda';
import { AuthUser, HTTPError, Patient, QnAType, QuestionAnswers } from 'types/types';
import Tooltip from 'components/Tooltip/Tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendPingMessage, startVideoCall, updateQuestionnaire } from 'api/api';
import { Ping } from 'components/Alert/Alert';
import { dequal } from 'dequal';
import {
  askQnAsListenerQuery,
  askQnAsQuery,
  questionnairesQuery,
} from 'pages/PatientRecords/PatientRecords';
import { PAGE_TABS } from 'components/VisitRecords/VisitRecords';
import { VISIT_STATUS } from 'context/visitContext';
import moment from 'moment';
import QnAConvo from 'components/QnAConvo/QnAConvo';
import CallPrompt from 'components/Modal/CallPrompt/CallPrompt';
import { LOAD_MESSAGES_INTERVAL } from 'context/msgContext';
import { showAlert, useAppContext, usePatient } from '../../context/appContext';

type AttachedFile = {
  id: string;
  name: string;
  key: string;
  url: string;
  file?: File;
  tempURL?: string;
  isUploadingFile?: boolean;
};

function VisitChat() {
  const [{ user }, dispatch] = useAppContext();
  const [showPingAlert, setShowPingAlert] = React.useState(false);
  const [showCallPrompt, setShowCallPrompt] = React.useState<'VOICE' | 'VIDEO'>();
  const { patient } = usePatient();
  const { visitId = '' } = useParams<{ visitId: string }>();
  const messagesWrapperRef = React.useRef<HTMLDivElement>(null);
  const lastMessageRef = React.useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const { data } = useQuery({
    ...questionnairesQuery({ id: visitId }),
    enabled: !!(isEmpty(patient?.questionnaires) && visitId),
  });

  const questionnaire = React.useMemo(() => {
    const questionnaires = (data || patient?.questionnaires || []) as QuestionAnswers[];
    return questionnaires.find(fndQuestionnaire => fndQuestionnaire.id === visitId);
  }, [data, patient?.questionnaires, visitId]);

  const { data: qnAs, isLoading } = useQuery({
    ...askQnAsQuery(visitId),
    enabled: !!visitId,
  });

  const { groupedQnAs, notAnsweredQnAs } = React.useMemo(() => {
    const QnAs = (qnAs || []) as QnAType[];
    if (!QnAs) return {};
    return {
      notAnsweredQnAs: QnAs[QnAs.length - 1],
      groupedQnAs: groupByDate(sortWith([ascend(prop('createdAt') as any)], QnAs)) as Record<
        string,
        QnAType[]
      >,
    };
  }, [qnAs]);

  const { data: listenedQnAsQuery } = useQuery({
    ...askQnAsListenerQuery(visitId, notAnsweredQnAs?.id),
    refetchInterval: LOAD_MESSAGES_INTERVAL,
    enabled: !!(notAnsweredQnAs?.id && visitId),
  });

  const { mutate: sendMessageFn } = useMutation(
    (variables: { vVisitId: string; payload: Record<string, any> }) =>
      updateQuestionnaire(variables.vVisitId, variables.payload),
    {
      onMutate: async variables => {
        const { queryKey } = askQnAsQuery(variables.vVisitId);
        await queryClient.cancelQueries({ queryKey });
        let oldQnAs = queryClient.getQueryData<QnAType[]>(queryKey);

        const tempMessage = {
          id: randomTempIdGenerator(),
          content: variables.payload.additionalQuestions[0].question,
          createdAt: moment().format(),
          isSender: true,
        };

        oldQnAs = [...(oldQnAs || []), tempMessage];

        queryClient.setQueryData(queryKey, oldQnAs);

        return { oldQnAs };
      },
      onSettled: (_, error, variables, context) => {
        const { queryKey } = askQnAsQuery(variables.vVisitId);
        if (error) {
          if (context?.oldQnAs) {
            queryClient.setQueryData(queryKey, context?.oldQnAs);
          }
          showAlert(dispatch, {
            show: true,
            message: (error as HTTPError).message,
            type: 'ERROR',
          });
        }
        queryClient.invalidateQueries({ queryKey });
      },
    },
  );

  const { mutate: startVideoCallFn } = useMutation((vVisitId: string) => startVideoCall(vVisitId), {
    onMutate: async vVisitId => {
      const { queryKey } = askQnAsQuery(vVisitId);
      await queryClient.cancelQueries({ queryKey });
      let oldQnAs = queryClient.getQueryData<QnAType[]>(queryKey);

      const uuid = randomTempIdGenerator();

      const tempMessage = {
        id: uuid,
        content: 'LOADING-VIDEO-LINK',
        createdAt: moment().format(),
        isSender: true,
        tempId: uuid,
      };

      oldQnAs = [...(oldQnAs || []), tempMessage];

      queryClient.setQueryData(queryKey, oldQnAs);

      return { oldQnAs };
    },
    onSettled: (_, error, vVisitId, context) => {
      const { queryKey } = askQnAsQuery(vVisitId);
      if (error) {
        if (context?.oldQnAs) {
          queryClient.setQueryData(queryKey, context?.oldQnAs);
        }
        showAlert(dispatch, {
          show: true,
          message: (error as HTTPError).message,
          type: 'ERROR',
        });
      }
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: sendPingFn } = useMutation(
    (questionnaireId: string) => sendPingMessage(questionnaireId),
    {
      onMutate: () => {
        setShowPingAlert(true);
      },
      onSettled: (_, error) => {
        if (error) {
          showAlert(dispatch, {
            show: true,
            message: (error as HTTPError).message,
            type: 'ERROR',
          });
          setShowPingAlert(false);
          return;
        }
        setTimeout(() => {
          setShowPingAlert(false);
        }, 3000);
      },
    },
  );

  const { mutate: markAnsweredQnAsAsReadFn } = useMutation(
    (variables: { vVisitId: string; payload: Record<string, any> }) =>
      updateQuestionnaire(variables.vVisitId, variables.payload),
    {
      onSettled: (_, error, variables) => {
        const { queryKey } = askQnAsQuery(variables.vVisitId);
        queryClient.invalidateQueries({ queryKey });
      },
    },
  );

  React.useEffect(() => {
    const latestQnAsId = qnAs?.[qnAs.length - 1]?.id || '';
    const element = document.getElementById(latestQnAsId) as HTMLDivElement;
    if (element) {
      lastMessageRef.current = element;
      scrollToLastMessage();
    }
  }, [qnAs]);

  React.useEffect(() => {
    const latestQnAs = groupedQnAs?.Today?.[groupedQnAs.Today.length - 1];

    if (!dequal(latestQnAs, listenedQnAsQuery) && visitId) {
      const { queryKey } = askQnAsQuery(visitId);
      queryClient.invalidateQueries({ queryKey });
    }
  }, [groupedQnAs?.Today, listenedQnAsQuery, queryClient, visitId]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (!(visitId && (listenedQnAsQuery?.id || params.get('QnAsId')))) return;

    markAnsweredQnAsAsReadFn({
      vVisitId: visitId,
      payload: {
        id: visitId,
        additionalQuestions: [
          {
            id: listenedQnAsQuery?.id || params.get('QnAsId'),
            answerReadAt: moment().toISOString(),
          },
        ],
      },
    });
  }, [listenedQnAsQuery?.id, location.search, markAnsweredQnAsAsReadFn, visitId]);

  const handleSendTextMessage = (message: string) => {
    if (!user || !visitId) return;
    sendMessageFn({
      vVisitId: visitId,
      payload: {
        id: visitId,
        additionalQuestions: [
          {
            answer: '',
            question: message,
          },
        ],
      },
    });
  };

  const handleNextTab = () => {
    const tab = 'chat';

    const indexOfActiveTab = PAGE_TABS.findIndex(fndTab => fndTab.id === tab);

    const nextTab = PAGE_TABS.find((_, index) => index === indexOfActiveTab + 1);

    if (nextTab) {
      const tabURL = `${location.pathname.replace(tab, nextTab.id)}${location.search}`;
      navigate(tabURL);
    }
  };

  const scrollToLastMessage = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView(true);
    }
  };

  return (
    <div className="flex flex-col pt-0 pb-0 tablet:pt-10 tablet:pb-7">
      <div className="fs-exclude rounded-[15px] h-[600px] border border-zinc-200 flex flex-col transition-colors duration-300 bg-white">
        {isLoading ? (
          <div className="flex flex-col w-full h-full justify-center overflow-hidden">
            <div className="flex flex-col w-fit h-full mx-auto justify-center overflow-hidden">
              <Icon id="loader" className="w-10 h-10 text-gray-300 mx-auto" />
              <span>Loading conversation with {patient.firstName || 'patient'}...</span>
            </div>
          </div>
        ) : !groupedQnAs || isEmpty(groupedQnAs) ? (
          <div className="flex flex-col w-full h-full justify-center items-center overflow-hidden">
            <ChatEmptyState
              patient={patient}
              questionnaire={questionnaire}
              user={user}
              onVideoCall={() => setShowCallPrompt('VIDEO')}
            />
          </div>
        ) : (
          <div
            ref={messagesWrapperRef}
            id="message__scroller"
            className="flex-col flex-1 pb-10 pt-5 px-5 tablet:px-8 overflow-y-auto relative"
          >
            <QnAConvo qnAs={groupedQnAs} patient={patient} />
          </div>
        )}
        <div
          id="message__inputs"
          className="border-t border-zinc-200 pl-4 pr-6 tablet:pl-7 tablet:pr-9 pt-1 pb-3 relative"
        >
          {(isLoading ||
            questionnaire?.status === VISIT_STATUS.COMPLETE ||
            isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')) && (
            <span
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
            bg-zinc-200/20 block w-full h-full z-10 cursor-not-allowed"
            />
          )}

          <ChatInput
            onSendTextMessage={handleSendTextMessage}
            onStartCall={call => setShowCallPrompt(call)}
            onPingPatient={() => sendPingFn(visitId)}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => handleNextTab()}
        className="w-fit border border-river-blue text-river-blue rounded-full px-5 py-[8px] text-sm mt-14 min-w-[180px] 
            hover:bg-[#EEFBFF]"
      >
        Next
      </button>

      <Ping show={showPingAlert} />

      {showCallPrompt && (
        <CallPrompt
          patient={patient}
          call={showCallPrompt}
          onCloseModal={() => setShowCallPrompt(undefined)}
          onStartVideoCall={() => {
            setShowCallPrompt(undefined);
            if (showCallPrompt === 'VOICE') {
              window.location.href = `tel:${patient}`;
            } else {
              startVideoCallFn(visitId);
            }
          }}
        />
      )}
    </div>
  );
}

function ChatEmptyState({
  patient,
  questionnaire,
  user,
  onVideoCall,
}: {
  patient: Patient;
  questionnaire?: QuestionAnswers;
  user: AuthUser | null;
  onVideoCall: () => void;
}) {
  return (
    <div
      className="flex flex-col w-full tablet:w-[45%] h-full mx-auto justify-center overflow-hidden 
          place-items-center text-center"
    >
      <span
        className="border flex flex-row justify-center items-center w-[70px] h-[70px] rounded-full mb-4
        bg-[#FFF8ED66]/40 border-river-deep-orange/40"
      >
        <IconSprite id="icon-caller" className="w-[35px] h-[35px] text-river-deep-orange" />
      </span>

      <p className="mb-4 w-full tablet:w-[90%]">
        {patient.phoneNumber
          ? `If you have any questions regarding ${patient.firstName} ${patient.lastName}'s visit, feel free to message her through the chat box below or contact the patient at`
          : 'This patient appears to be a dependent (minor). For more information, feel free to message the family manager through the chat box below or contact by phone at'}
      </p>

      <div className="border-y py-2 w-[80%] mb-14">
        <a href={`tel:${patient.phoneNumber}`} className="text-river-black font-bold text-lg">
          {formatPhoneNumber(patient.phoneNumber || patient?.familyManager?.phoneNumber || '')}
        </a>
      </div>

      <div className="">
        <p className="mb-3">Or start a video visit</p>
        <button
          type="button"
          onClick={onVideoCall}
          disabled={questionnaire?.providerUserId !== user?.id}
          className="flex flex-row w-fit bg-river-blue text-white rounded-full pl-[17px] pr-9 py-[2px]
          min-w-[180px] items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon id="iconChatCardVideoCall" className="w-[42px] h-[42px] text-white" />
          Start video visit
        </button>
      </div>
    </div>
  );
}

function ChatInput({
  onSendTextMessage,
  onPingPatient,
  onStartCall,
}: {
  onSendTextMessage: (message: string) => void;
  onStartCall: (call: 'VOICE' | 'VIDEO') => void;
  onPingPatient: () => void;
}) {
  const [messageText, setMessageText] = React.useState('');
  const [attachFiles, setAttachFiles] = React.useState<AttachedFile[] | undefined>([]);

  const handleSendMessage = () => {
    // if (messageText && attachFiles && attachFiles.length > 0) {
    //   let content = messageText.trim();
    //   const attachedFilesURL = `[${pluck('url', attachFiles).join(',')}]`;
    //   content += ` <${FILE_PATTERN_KEY}:${attachedFilesURL}>`;
    //   onSendTextMessage(content);
    // }

    if (messageText && !(attachFiles && attachFiles.length > 0)) {
      const content = messageText.trim();
      onSendTextMessage(content);
    }

    setMessageText('');
  };

  // const handleUploadedFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!event.target.files || event.target.files.length === 0) return;

  //   let files: AttachedFile[] = (Array.from(event.target.files) || []).map(file => ({
  //     file,
  //     id: `${file.lastModified}${file.size}`,
  //     name: file.name,
  //     key: getFileKey(file.name),
  //     url: '',
  //     tempURL: URL.createObjectURL(file),
  //     isUploadingFile: true,
  //   }));

  //   files = [...(attachFiles || []), ...files];

  //   setAttachFiles(files);

  //   const uploadedFiles = await Promise.all(
  //     files.map(async findAttached => {
  //       if ((findAttached.url && !isEmpty(findAttached.url)) || !findAttached.file) {
  //         return {
  //           ...findAttached,
  //           isUploadingFile: false,
  //         };
  //       }

  //       const resizedFile = findAttached.file;

  //       return {
  //         ...findAttached,
  //         url: (await uploadAWSS3File(
  //           resizedFile,
  //           findAttached.key,
  //           resizedFile.type,
  //           'public',
  //         )) as string,
  //         isUploadingFile: false,
  //       };
  //     }),
  //   );

  //   if (!dequal(files, uploadedFiles)) {
  //     setAttachFiles(uploadedFiles);
  //   }

  //   event.target.files = null;
  //   event.target.value = '';
  // };

  // const getFileKey = (filename: string) => `chat/${uuidv4()}/${filename}`;

  return (
    <>
      {attachFiles && attachFiles.length > 0 ? (
        <div className="mx-2 mt-2 flex flex-row flex-wrap gap-2">
          {attachFiles.map(file => (
            <AttachedFilePreview
              file={file}
              onRemoveFile={() =>
                setAttachFiles(prevAttachedFiles =>
                  prevAttachedFiles?.filter(fndAttachedFile => fndAttachedFile.id !== file.id),
                )
              }
            />
          ))}
        </div>
      ) : null}

      <div className="leading-[0]">
        <AutoSizeTextBoxInput
          className="bg-transparent pl-[10px] px-0 pb-[6px] rounded-none text-sm tablet:text-[15px] border-none 
          ring-0 focus:ring-0 leading-relaxed"
          name="messageInput"
          placeholder="Your message..."
          value={messageText}
          onChange={event => setMessageText(event.target.value)}
          onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' || event.keyCode === 13) {
              event.preventDefault();

              if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
                handleSendMessage();
              } else {
                const { selectionStart, selectionEnd } = event.target as HTMLTextAreaElement;
                const newText = `${messageText.slice(0, selectionStart)}\n${messageText.slice(
                  selectionEnd,
                )}`;
                setMessageText(newText);
                // Move the cursor to the new position (current position + 1)
                (event.target as HTMLTextAreaElement).setSelectionRange(
                  selectionStart + 1,
                  selectionStart + 1,
                );
              }
            }
          }}
        />
      </div>

      <div className="flex flex-row justify-between items-end">
        <div className="inline-flex">
          <button
            onClick={() => onPingPatient()}
            type="button"
            className="hover:bg-gray-100 group/ping rounded-full w-9 h-9 justify-center items-center flex relative"
          >
            <IconSprite
              id="icon-bell"
              className="w-[22px] h-[22px] text-zinc-400 stroke-zinc-400"
            />
            <Tooltip
              content="Ping patient"
              className="!absolute left-0 right-0 top-[30px]
              group-hover/ping:after:!inline-block after:left-0 after:px-[12px] after:text-[13px] after:py-[4px] after:shadow after:rounded-[10px]
              group-hover/ping:before:!block before:!left-[20px]"
            />
          </button>

          <span className="inline border-l mx-2 h-6 self-center" />

          <button
            onClick={() => onStartCall('VOICE')}
            type="button"
            className="hidden hover:bg-gray-100 group/video rounded-full w-9 h-9 justify-center items-center tablet:flex relative"
          >
            <IconSprite
              id="icon-phone"
              className="w-[22px] h-[22px] text-zinc-400 stroke-zinc-400"
            />
            <Tooltip
              content="Start Voice Call"
              className="!absolute left-0 right-0 top-[30px]
                  group-hover/video:after:!inline-block after:left-0 after:px-[12px] after:text-[13px] after:py-[4px] after:shadow after:rounded-[10px]
                  group-hover/video:before:!block before:!left-[20px]"
            />
          </button>

          <span className="inline border-l mx-2 h-6 self-center" />

          <button
            onClick={() => onStartCall('VIDEO')}
            type="button"
            className="hidden hover:bg-gray-100 group/video rounded-full w-9 h-9 justify-center items-center tablet:flex relative"
          >
            <IconSprite
              id="icon-video-camera"
              className="w-[22px] h-[22px] text-zinc-400 stroke-zinc-400"
            />
            <Tooltip
              content="Start video Call"
              className="!absolute left-0 right-0 top-[30px]
                  group-hover/video:after:!inline-block after:left-0 after:px-[12px] after:text-[13px] after:py-[4px] after:shadow after:rounded-[10px]
                  group-hover/video:before:!block before:!left-[20px]"
            />
          </button>

          {/* <span className="hidden tablet:inline border-l mx-2 h-6 self-center" />

          <button
            type="button"
            className="hover:bg-gray-100 group/photo rounded-full w-9 h-9 justify-center items-center flex relative"
          >
            <IconSprite
              id="icon-photo"
              className="w-[22px] h-[22px] text-zinc-400 stroke-zinc-400"
            />
            <input
              type="file"
              accept="image/*"
              className="rounded-full w-10 file:cursor-pointer appearance-none absolute opacity-0"
              onChange={handleUploadedFile}
              multiple
            />
            <Tooltip
              content="Attach Photo"
              className="!absolute left-0 right-0 top-[30px]
              group-hover/photo:after:!inline-block after:left-0 after:px-[12px] after:text-[13px] after:py-[4px] after:shadow after:rounded-[10px]
              group-hover/photo:before:!block before:!left-[20px]"
            />
          </button>

          <span className="border-l mx-2 h-6 self-center" />

          <button
            type="button"
            className="hover:bg-gray-100 group/file rounded-full w-9 h-9 justify-center items-center flex relative"
          >
            <IconSprite
              id="icon-paperclip"
              className="w-[22px] h-[22px] text-zinc-400 stroke-zinc-400"
            />
            <input
              type="file"
              accept=".doc,.docx,.pdf"
              className="rounded-full w-10 file:cursor-pointer appearance-none absolute opacity-0"
              onChange={handleUploadedFile}
              multiple
            />
            <Tooltip
              content="Attach File"
              className="!absolute left-0 right-0 top-[30px]
              group-hover/file:after:!inline-block after:left-0 after:px-[12px] after:text-[13px] after:py-[4px] after:shadow after:rounded-[10px]
              group-hover/file:before:!block before:!left-[20px]"
            />
          </button> */}
        </div>
        <div>
          <button
            onClick={() => handleSendMessage()}
            type="button"
            className={`w-[42px] h-[42px] rounded-full justify-center inline-flex items-center ${
              messageText ? 'bg-river-blue' : 'bg-[#ECF6FB]'
            }`}
          >
            <IconSprite
              id="icon-send"
              className={`w-5 h-5 ml-1 ${
                messageText ? 'text-white stroke-white' : 'text-river-blue stroke-river-blue'
              }`}
            />
          </button>
        </div>
      </div>
    </>
  );
}

function AttachedFilePreview({
  file,
  onRemoveFile,
}: {
  file: AttachedFile;
  onRemoveFile: () => void;
}) {
  return (
    <div className="group relative inline-block" key={file.id}>
      <div className="relative overflow-hidden rounded-xl border border-gray-200 hover:opacity-80">
        {getFileType(file.name) === 'image' ? (
          <div className="h-16 w-16">
            <button type="button" className="h-full w-full">
              <span
                className="flex items-center h-full w-full justify-center bg-gray-500 bg-cover bg-center text-white"
                style={{
                  backgroundImage: `url(${file.tempURL})`,
                }}
              />
            </button>
          </div>
        ) : (
          <div className="h-16 w-[230px] flex flex-row space-x-2 cursor-pointer">
            <button type="button" className="h-16 w-16 shrink-0">
              <span
                className={`flex items-center h-full w-full justify-center bg-cover bg-center text-white ${
                  file.name.toLocaleLowerCase().endsWith('.pdf') ? 'bg-[#E01E5A]' : 'bg-[#1264a4]'
                }`}
              >
                <IconSprite
                  id={
                    file.name.toLocaleLowerCase().endsWith('.pdf')
                      ? 'icon-pdf'
                      : file.name.toLocaleLowerCase().endsWith('.doc') ||
                        file.name.toLocaleLowerCase().endsWith('.docx')
                      ? 'icon-docx'
                      : 'icon-paper-file'
                  }
                  className={`flex items-center text-white ${
                    file.name.toLocaleLowerCase().endsWith('.pdf')
                      ? 'h-[35px] w-[35px]'
                      : file.name.toLocaleLowerCase().endsWith('.doc') ||
                        file.name.toLocaleLowerCase().endsWith('.docx')
                      ? 'h-[40px] w-[40px]'
                      : 'h-[30px] w-[30px]'
                  }`}
                />
              </span>
            </button>
            <span className="w-full flex-1 justify-center flex flex-col align-middle leading-[22px] overflow-hidden">
              <span className="block text-[15px] font-medium whitespace-pre overflow-hidden text-ellipsis">
                {file.name}
              </span>
              <span className="text-[13px] block text-zinc-400 font-light">
                {file.name.toLocaleLowerCase().endsWith('.pdf')
                  ? 'PDF'
                  : file.name.toLocaleLowerCase().endsWith('.doc') ||
                    file.name.toLocaleLowerCase().endsWith('.docx')
                  ? 'Word Document'
                  : `${getFileExtension(file.name)?.toLocaleUpperCase()} File`}
              </span>
            </span>
          </div>
        )}
      </div>
      <button
        onClick={() => onRemoveFile()}
        disabled={isEmpty(file.url) && file.isUploadingFile}
        type="button"
        className={`absolute right-1 top-1 -translate-y-1/2 translate-x-1/2 rounded-full border border-white bg-river-black 
      text-white transition-colors hover:bg-river-black group-hover:opacity-100 ${
        isEmpty(file.url) && file.isUploadingFile ? 'opacity-100 p-[1px]' : 'opacity-0 p-[6px]'
      }`}
      >
        {isEmpty(file.url) && file.isUploadingFile ? (
          <Icon id="loader" className="w-[18px] h-[18px] text-river-blue" />
        ) : (
          <Icon id="close" className="w-[8px] h-[8px] text-white stroke-1" />
        )}
      </button>
    </div>
  );
}

export default VisitChat;
